import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function WhoWeArePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Who We Are</h1>
          <p className="text-xl md:text-2xl max-w-3xl font-light">
            CEB Consulting Inc. - Your trusted partner in business transformation with over 20 years of consulting excellence in Toronto.
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">Our Story</h2>
              <div className="w-24 h-1 bg-teal-500 mb-6"></div>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                <p>
                  Founded in Toronto over two decades ago, CEB Consulting Inc. has grown from a small advisory practice into a trusted consulting partner for businesses across Canada and beyond.
                </p>
                <p>
                  Our journey began with a simple mission: to help businesses navigate complex challenges and unlock their full potential. Today, we've helped over 500 organizations achieve measurable growth and sustainable success.
                </p>
                <p>
                  Based in the heart of Toronto, we bring deep industry expertise, proven methodologies, and a client-centered approach to every engagement. Our team of experienced consultants has worked across diverse sectors, delivering results that matter.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://www.simon-kucher.com/sites/default/files/styles/max_1300x1300/public/paragraph-careers-large-text-media/2025-12/testimonials_teaser_resized_1_0.png.webp?itok=Rs8Jxeu9"
                  alt="Our Team"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white p-10 rounded-2xl shadow-lg">
              <div className="w-16 h-16 bg-teal-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-compass-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4 text-gray-900">Our Mission</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                To empower businesses with strategic insights and actionable solutions that drive sustainable growth, operational excellence, and competitive advantage in an ever-evolving marketplace.
              </p>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-lg">
              <div className="w-16 h-16 bg-teal-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4 text-gray-900">Our Vision</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                To be the most trusted consulting partner for businesses seeking transformational growth, recognized for our deep expertise, innovative approaches, and unwavering commitment to client success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Our Core Values</h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do and define how we work with our clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Integrity',
                description: 'We operate with the highest ethical standards and transparency in all our engagements.'
              },
              {
                icon: 'ri-lightbulb-line',
                title: 'Innovation',
                description: 'We continuously seek creative solutions and embrace new approaches to solve complex challenges.'
              },
              {
                icon: 'ri-trophy-line',
                title: 'Excellence',
                description: 'We are committed to delivering exceptional quality and measurable results in every project.'
              },
              {
                icon: 'ri-team-line',
                title: 'Partnership',
                description: 'We build long-term relationships based on trust, collaboration, and mutual success.'
              }
            ].map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-4xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Why Choose CEB Consulting Inc.</h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto mb-6"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-time-line',
                title: '20+ Years Experience',
                description: 'Over two decades of proven consulting excellence helping businesses across Toronto and Canada achieve their strategic objectives.'
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Toronto-Based Expertise',
                description: 'Deep understanding of the Canadian market with global best practices and local insights.'
              },
              {
                icon: 'ri-user-heart-line',
                title: 'Client-Centered Approach',
                description: 'We build long-term partnerships focused on your success, not just project completion.'
              },
              {
                icon: 'ri-bar-chart-line',
                title: 'Measurable Results',
                description: 'Data-driven strategies that deliver tangible outcomes and sustainable competitive advantage.'
              },
              {
                icon: 'ri-team-line',
                title: 'Industry Expertise',
                description: 'Specialized knowledge across technology, finance, healthcare, manufacturing, and professional services.'
              },
              {
                icon: 'ri-award-line',
                title: 'Proven Methodologies',
                description: 'Battle-tested frameworks and approaches refined through hundreds of successful engagements.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
